import { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiEye, FiEyeOff, FiSave, FiCheckCircle, FiAlertCircle } from 'react-icons/fi'

export default function PaymentConfigModal({ websiteId, title, onClose }) {
  const [activeTab, setActiveTab] = useState('paystack')
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [showSecrets, setShowSecrets] = useState(false)

  // Paystack
  const [paystackForm, setPaystackForm] = useState({
    publicKey: '',
    secretKey: '',
    currency: 'NGN',
    acceptPayments: false
  })

  // Telegram
  const [telegramForm, setTelegramForm] = useState({
    botToken: '',
    chatId: '',
    groupName: '',
    enabled: false
  })

  useEffect(() => {
    fetchConfigs()
  }, [websiteId])

  const fetchConfigs = async () => {
    try {
      const [paystackRes, telegramRes] = await Promise.all([
        axios.get(`/website-payment-config/paystack/${websiteId}`),
        axios.get(`/website-payment-config/telegram/${websiteId}`)
      ])

      if (paystackRes.data.paystackConfig) {
        setPaystackForm({
          publicKey: paystackRes.data.paystackConfig.publicKey || '',
          secretKey: paystackRes.data.paystackConfig.secretKey || '',
          currency: paystackRes.data.paystackConfig.currency || 'NGN',
          acceptPayments: paystackRes.data.paystackConfig.acceptPayments || false
        })
      }

      if (telegramRes.data.telegramConfig) {
        setTelegramForm({
          botToken: telegramRes.data.telegramConfig.botToken || '',
          chatId: telegramRes.data.telegramConfig.chatId || '',
          groupName: telegramRes.data.telegramConfig.groupName || '',
          enabled: telegramRes.data.telegramConfig.enabled || false
        })
      }
    } catch (error) {
      console.error('Error fetching configs:', error)
    }
  }

  const handlePaystackChange = (e) => {
    const { name, value, checked, type } = e.target
    setPaystackForm({
      ...paystackForm,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const handleTelegramChange = (e) => {
    const { name, value, checked, type } = e.target
    setTelegramForm({
      ...telegramForm,
      [name]: type === 'checkbox' ? checked : value
    })
  }

  const savePaystackConfig = async () => {
    if (!paystackForm.publicKey || !paystackForm.secretKey) {
      toast.error('Paystack public and secret keys are required')
      return
    }

    try {
      setLoading(true)
      await axios.put(`/website-payment-config/paystack/${websiteId}`, paystackForm)
      toast.success('Paystack configuration saved successfully')
      fetchConfigs()
    } catch (error) {
      console.error('Error saving Paystack config:', error)
      toast.error(error.response?.data?.message || 'Failed to save Paystack configuration')
    } finally {
      setLoading(false)
    }
  }

  const saveTelegramConfig = async () => {
    if (telegramForm.enabled && (!telegramForm.botToken || !telegramForm.chatId)) {
      toast.error('Bot token and chat ID are required when enabling Telegram')
      return
    }

    try {
      setLoading(true)
      await axios.put(`/website-payment-config/telegram/${websiteId}`, telegramForm)
      toast.success('Telegram configuration saved successfully')
      fetchConfigs()
    } catch (error) {
      console.error('Error saving Telegram config:', error)
      toast.error(error.response?.data?.message || 'Failed to save Telegram configuration')
    } finally {
      setLoading(false)
    }
  }

  const testTelegramConnection = async () => {
    if (!telegramForm.botToken || !telegramForm.chatId) {
      toast.error('Bot token and chat ID are required')
      return
    }

    try {
      setTesting(true)
      const response = await axios.post(`/website-payment-config/telegram/${websiteId}/test`, {
        botToken: telegramForm.botToken,
        chatId: telegramForm.chatId
      })

      if (response.data.success) {
        toast.success(response.data.message)
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      console.error('Error testing Telegram:', error)
      toast.error('Failed to test Telegram connection')
    } finally {
      setTesting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 pt-4">
          <button
            onClick={() => setActiveTab('paystack')}
            className={`px-4 py-3 border-b-2 font-medium transition-colors ${
              activeTab === 'paystack'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Paystack Payment
          </button>
          <button
            onClick={() => setActiveTab('telegram')}
            className={`px-4 py-3 border-b-2 font-medium transition-colors ${
              activeTab === 'telegram'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Telegram Notifications
          </button>
        </div>

        <div className="p-6">
          {/* PAYSTACK TAB */}
          {activeTab === 'paystack' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Secure Credential Storage</p>
                    <p className="mt-1">All Paystack credentials are encrypted and stored securely. They are never exposed to the frontend or logs.</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paystack Public Key *
                </label>
                <input
                  type="password"
                  value={paystackForm.publicKey}
                  onChange={handlePaystackChange}
                  name="publicKey"
                  placeholder="pk_live_..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">Get this from your Paystack Dashboard</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paystack Secret Key *
                </label>
                <div className="relative">
                  <input
                    type={showSecrets ? 'text' : 'password'}
                    value={paystackForm.secretKey}
                    onChange={handlePaystackChange}
                    name="secretKey"
                    placeholder="sk_live_..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecrets(!showSecrets)}
                    className="absolute right-3 top-2.5 text-gray-500"
                  >
                    {showSecrets ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Keep this secret! Never share it.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                  <select
                    value={paystackForm.currency}
                    onChange={handlePaystackChange}
                    name="currency"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="NGN">NGN (₦ Nigerian Naira)</option>
                    <option value="USD">USD ($ US Dollar)</option>
                    <option value="GHS">GHS (₵ Ghana Cedi)</option>
                    <option value="ZAR">ZAR (R South African Rand)</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={paystackForm.acceptPayments}
                      onChange={handlePaystackChange}
                      name="acceptPayments"
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-700">Accept Payments</span>
                  </label>
                </div>
              </div>

              <button
                onClick={savePaystackConfig}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors"
              >
                <FiSave className="w-4 h-4" /> Save Paystack Configuration
              </button>

              {paystackForm.acceptPayments && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium">Payments Enabled</p>
                    <p className="mt-1">Your store is ready to accept payments via Paystack.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TELEGRAM TAB */}
          {activeTab === 'telegram' && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">How to Set Up Telegram Notifications</p>
                    <ol className="mt-2 space-y-1 list-decimal list-inside">
                      <li>Create a Telegram bot using @BotFather</li>
                      <li>Get your bot token from @BotFather</li>
                      <li>Create/find a Telegram group and add your bot</li>
                      <li>Get your chat ID (use @userinfobot in the group)</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={telegramForm.enabled}
                    onChange={handleTelegramChange}
                    name="enabled"
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">Enable Telegram Notifications</span>
                </label>
              </div>

              {telegramForm.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bot Token *
                    </label>
                    <input
                      type="password"
                      value={telegramForm.botToken}
                      onChange={handleTelegramChange}
                      name="botToken"
                      placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Chat ID *
                    </label>
                    <input
                      type="text"
                      value={telegramForm.chatId}
                      onChange={handleTelegramChange}
                      name="chatId"
                      placeholder="-123456789"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Group Name (Display Only)
                    </label>
                    <input
                      type="text"
                      value={telegramForm.groupName}
                      onChange={handleTelegramChange}
                      name="groupName"
                      placeholder="e.g., Order Notifications"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={testTelegramConnection}
                      disabled={testing}
                      className="flex-1 px-4 py-2 border border-primary-600 text-primary-600 hover:bg-primary-50 disabled:bg-gray-100 rounded-lg font-medium transition-colors"
                    >
                      {testing ? 'Testing...' : 'Test Connection'}
                    </button>
                    <button
                      onClick={saveTelegramConfig}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors"
                    >
                      <FiSave className="w-4 h-4" /> Save
                    </button>
                  </div>
                </>
              )}

              {!telegramForm.enabled && (
                <button
                  onClick={saveTelegramConfig}
                  className="w-full px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                >
                  Disable Telegram Notifications
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
